import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import '../../highcharts-options';

import { getChartById, getKeyTime, highchartsPalette, getUnits, getCoarsestFrequency, colStat } from '../../../utils';
import * as utils from '../../../utils';
import { update as interUpdate, callbackGlobStat as interCallbackGlobStat } from '../../../modules/intercomp';

import chartsStyle from '../../../../public/config/charts-style.json';

import { Aeroval } from '../../../types/global';
import { RegStatData, TrendsStats } from '../../../types/data';
declare var aeroval: Aeroval;

export function plotRegionalStatisticsMultiModel(data: RegStatData, statisticDir: string, parameterDir: string, updateTS: boolean, updateScat: boolean, id = 'reg-stat') {
	//decompose statistics: support obs/mod_trends
	if (statisticDir.includes('/')) {
		var firstUnderscore = statisticDir.indexOf('_');
		var stat1 = `${statisticDir.split('/')[0]}_${statisticDir.slice(firstUnderscore + 1)}`;
		var stat2 = statisticDir.split('/')[1];
		var stats = [stat1, stat2];
	} else {
		var stats = [statisticDir];
	}

	//time selection
	const keyTime = getKeyTime(aeroval.time, aeroval.season);

	var series: any[] = [];
	var regionalStatistics = {} as any;
	const showIndividualModels = String(aeroval.settings.showIndividualModels) === 'true';

	const mods = Object.keys(data);
	for (let stat of stats) {
		if (showIndividualModels) {
			for (let j: number = 0; j < mods.length; j++) {
				const model = mods[j];

				if (stat == 'obs_trend' && j > 0) continue;

				var vis = true;
				if (typeof aeroval.seriesVisibility[mods[j]] != 'undefined') {
					vis = aeroval.seriesVisibility[mods[j]];
				}

				var modName = mods[j];
				var modVar = Object.keys(data[mods[j]]);
				var modData = data[mods[j]][modVar[0]];

				var seriesData = [];
				const highchartsColor = highchartsPalette(j);
				const modelStyle = aeroval.modelStyle[model];
				const colorModel = modelStyle?.color ? modelStyle.color : highchartsColor;

				if (stat == 'obs_trend') {
					var colorSeries = aeroval.settings.theme === "dark" ? chartsStyle['intercomp-ts'][`obs-primary`].colorDarkMode : chartsStyle['intercomp-ts'][`obs-primary`].color
					var coarsestFrequency = getCoarsestFrequency(aeroval.cfg.freqs)
					var seriesName = `Observation (${coarsestFrequency})`
				} else {
					colorSeries = colorModel
					seriesName = model
				}

				var i = 0;
				for (var region in aeroval.regions) {
					if (typeof modData?.[region]?.[keyTime] != 'undefined') {
						const label = getRegionLabel(region);
						const value = getRegionalStatisticValue(modData, region, keyTime, stat);

						if (aeroval.settings.showEmptyRegions === 'false' && (value === null || value === undefined)) {
							i++;
							continue;
						}

						regionalStatistics[region] = {
							statistic: value,
							nStations: modData[region]['num_coords_with_data'],
							nPoints: modData[region]['num_valid'],
						};

						if (typeof regionalStatistics[region]['statistic'] != 'undefined') {
							seriesData.push({
								category: i,
								y: regionalStatistics[region]['statistic'],
								model: modName,
								label,
							});
						} else {
							seriesData.push(null);
						}
					} else {
						regionalStatistics[region] = {
							statistic: null,
							nStations: null,
						};
					}
					i++;
				}

				series.push({
					name: seriesName,
					data: seriesData,
					color: colorSeries,
					showInLegend: false,
					visible: vis,
					dataLabels: {
						enabled: vis,
						allowOverlap: true,
						style: { fontSize: 8, fontWeight: 'bolder' },
						formatter: function (): string | undefined {
							if (aeroval.cfg.webdisp_opts.country_flag || aeroval.cfg.webdisp_opts.country_code) {
								var models = Object.keys(aeroval.menu[parameterDir]['obs'][aeroval.observation][aeroval.layer]);
								var chartSeries = getChartById('regStat')?.series;

								if (!chartSeries) return;

								var max_abs = 0;
								var showModel = '';

								for (let i in models) {
									var model = models[i];
									var serie = chartSeries[i];
									if (model in aeroval.seriesVisibility && !aeroval.seriesVisibility[model]) {
									} else {
										if (typeof serie.points != 'undefined') {
											for (var point of serie.points) {
												if (point.y && point.category === this.x) {
													if (Math.abs(point.y) > max_abs) {
														showModel = model;
														max_abs = Math.abs(point.y);
													}
												}
											}
										}
									}
								}
								if ((this as any).point.model === showModel) {
									return (this as any).point.label;
								}
							}
							return;
						},
						y: 0,
						x: 0,
					},
				});

				if (stat === 'obs_trends') {
					break;
				}
			}
		} else {
			const regionNames = Object.keys(aeroval.regions);
			const averagedSeriesData = [] as any[];
			const stdDevBandData = [] as [number, number, number][];

			for (let i = 0; i < regionNames.length; i++) {
				const region = regionNames[i];
				const label = getRegionLabel(region);
				const values = [] as number[];
				let nStations: number | null = null;
				let nPoints: number | null = null;

				for (let j = 0; j < mods.length; j++) {
					if (stat == 'obs_trend' && j > 0) continue;
					const model = mods[j];
					if (aeroval.seriesVisibility[model] === false) continue;

					const modVar = Object.keys(data[model]);
					const modData = data[model][modVar[0]];
					if (typeof modData?.[region]?.[keyTime] == 'undefined') continue;

					const value = getRegionalStatisticValue(modData, region, keyTime, stat);
					if (value === null || value === undefined || !Number.isFinite(value)) continue;

					values.push(value);
					nStations = Number(modData[region]['num_coords_with_data'] as any);
					nPoints = Number(modData[region]['num_valid'] as any);
				}

				if (values.length === 0) {
					if (aeroval.settings.showEmptyRegions !== 'false') {
						regionalStatistics[region] = {
							statistic: null,
							nStations,
							nPoints,
						};
						averagedSeriesData.push(null);
						stdDevBandData.push(null as any);
					}
					continue;
				}

				const stdDevValue = utils.standardDeviation(values) ?? 0;
				const averagedValue = aeroval.interModelStatistic.dir === 'median'
					? (utils.quantile(values, 0.5) ?? 0)
					: (utils.average(values) ?? 0);
				const categoryIndex = averagedSeriesData.length;

				regionalStatistics[region] = {
					statistic: averagedValue,
					nStations,
					nPoints,
				};

				const isSelectedRegion = region === aeroval.region || region === aeroval.station;
				const { colorStyle, borderColor, borderWidth } = getBarColorStyle(averagedValue, parameterDir, statisticDir, stat, stats, isSelectedRegion, aeroval.globalRegions.includes(region));
				averagedSeriesData.push({
					x: categoryIndex,
					category: categoryIndex,
					y: averagedValue,
					label,
					stdDev: stdDevValue,
					color: colorStyle,
					borderColor,
					borderWidth,
				});
				stdDevBandData.push([averagedValue - stdDevValue, averagedValue + stdDevValue] as any);
			}

			const statName = aeroval.interModelStatistic.dir === 'median' ? 'Median' : 'Mean';

			series.push({
				name: `${statName} Model`,
				data: averagedSeriesData,
				showInLegend: false,
				visible: true,
				dataLabels: {
					enabled: true,
					allowOverlap: true,
					style: { fontSize: 8, fontWeight: 'bolder' },
					formatter: function (): string | undefined {
						if (aeroval.cfg.webdisp_opts.country_flag || aeroval.cfg.webdisp_opts.country_code) {
							return (this as any).point.label;
						}
						return;
					},
					y: 0,
					x: 0,
				},
			});

			series.push({
				type: 'errorbar',
				name: 'Std Dev',
				showInLegend: false,
				visible: true,
				linkedTo: ':previous',
				color: 'gray',
				lineWidth: 1,
				data: stdDevBandData,
				enableMouseTracking: true,
				zIndex: 3,
			});
		}
	}

	const unit = getUnits();
	const titleText = getTitle();

	Highcharts.chart(
		id,
		{
			chart: {
				renderTo: id,
				type: 'column',
				style: {
					cursor: 'pointer',
				}
			},
			title: {
				text: titleText,
			},
			subtitle: {
				text: `${aeroval.frequency} data - all stations`,
			},
			xAxis: {
				categories: Object.keys(regionalStatistics),
				crosshair: true,
				labels: {
					overflow: 'justify',
					rotation: -45,
				},
				cursor: 'pointer',
			} as Highcharts.XAxisOptions,
			yAxis: [
				{
					title: {
						text: `${aeroval.statistic.name} ${unit}`,
					},
				},
				{
					gridLineWidth: 0,
					title: {
						text: 'Nb. of Stations (◆)',
					},
					opposite: true,
					visible: false,
				},
			],
			plotOptions: {
				column: {
					turboThreshold: 0,
					borderWidth: 0,
					shadow: false,
					cursor: 'pointer',
					point: {
						events: {
							mouseOver: function () {
								if (aeroval.settings.presentationMode !== 'true') {
									const thisPoint = (this as any).point;
									const mapRegion = getChartById('map')?.get(thisPoint.category.name) as Highcharts.Series;
									const mapRegionName = mapRegion.userOptions.id
									if (mapRegionName == aeroval.region) return
									const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
									if (hoverLoaderRegionalChart instanceof HTMLElement) {
										hoverLoaderRegionalChart.classList.remove('hide');
										hoverLoaderRegionalChart.classList.add('show');
									}
									aeroval.wait = false;
									aeroval.timeOver = setTimeout(function () {
										if (aeroval.wait === false && mapRegion) {
											interUpdate('region', thisPoint.category.name);
											const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
											if (hoverLoaderRegionalChart instanceof HTMLElement) {
												hoverLoaderRegionalChart.classList.remove('show');
												hoverLoaderRegionalChart.classList.add('hide');
											}
										}
									}, 1000);
								}
							},
							mouseOut: function () {
								if (!aeroval.isMobile) {
									aeroval.wait = true;
									clearTimeout(aeroval.timeOver);
									const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
									if (hoverLoaderRegionalChart instanceof HTMLElement) {
										hoverLoaderRegionalChart.classList.remove('show');
										hoverLoaderRegionalChart.classList.add('hide');
									}
								}
							},
							click: function () {
								const thisPoint = (this as any).point;
								aeroval.wait = false;
								clearTimeout(aeroval.timeOver);
								interUpdate('region', thisPoint.category.name);
								const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
								if (hoverLoaderRegionalChart instanceof HTMLElement) {
									hoverLoaderRegionalChart.classList.remove('show');
									hoverLoaderRegionalChart.classList.add('hide');
								}
							},
						},
					},
				} as any,
			},
			tooltip: {
				shared: true,
				formatter: function () {
					const thisPoint = (this as any).point;
					if (!thisPoint) return;
					if (!showIndividualModels) {
						const statName = aeroval.interModelStatistic.dir === 'median' ? 'Median' : 'Mean';
						const yValue = typeof thisPoint.y === 'number' ? thisPoint.y.toFixed(aeroval.statistic.decimals) : 'n/a';
						const stdDevValue = typeof thisPoint.stdDev === 'number' ? thisPoint.stdDev.toFixed(aeroval.statistic.decimals) : 'n/a';
						return `<b>${thisPoint.category.name}</b><br><span>${statName}: <b>${yValue}</b></span><br><span>Std Dev: <b>${stdDevValue}</b></span>`;
					}

					const mods = Object.keys(data);
					var h = `<b>${thisPoint.category.name}</b><br>`;
					for (let i: number = 0; i < mods.length; i++) {
						if (aeroval.seriesVisibility[mods[i]] === false) continue;
						// set model style
						const modelStyle = aeroval.modelStyle[mods[i]];
						const color = modelStyle?.color ? modelStyle.color : highchartsPalette(i)
						const point = (this as any).points?.find((p: any) => p.series.name === mods[i]);
						h += `<span style='color: ${color}'>● </span>
							<span>${mods[i]}: <b> ${point?.point.y?.toFixed(aeroval.statistic.decimals)} </b></span>`;

						if (i < mods.length - 1) h += '<br>';
					}
					return h;
				},
			},
			series,
		},
		function (chart) {
			// when the chart is rendered
			var h = `<table class='table table-hover table-condensed slideTable'>
			<thead>
				<tr>
					<th rowspan='1'>Region</th>
					<th rowspan='1'>Nb. of Stations</th>
					<th rowspan='1'>Nb. of Points</th>
					<th seriesDatapan='1'>${aeroval.statistic.name}</th>
				</tr>
			</thead>
				<tbody>`
			for (let region in regionalStatistics) {
				h += `<tr>
				<td>${region}</td>
				<td>${regionalStatistics[region]['nStations']}</td>
				<td>${regionalStatistics[region]['nPoints']}</td>`
				h += regionalStatistics[region]['statistic'] != null
					? `<td>${regionalStatistics[region]['statistic'].toFixed(2)}</td>`
					: `<td>${regionalStatistics[region]['statistic']}</td>`;
				h += `</tr>`;
			}
			h += `</tbody>
			</table>`

			const regStatTable = document.getElementById('reg-stat-table');
			if (regStatTable) regStatTable.innerHTML = h;

			interCallbackGlobStat(updateTS, updateScat);
			chart.redraw();
		}
	);
}

export function toggleRegionalStatisticsColor(value: string): void {
	aeroval.settings.regionalStatisticsColor = value;
}

function getBarColorStyle(value: number | null, parameterDir: string, statisticDir: string, stat: string, stats: string[], isSelectedRegion: boolean, isGlobalRegion: boolean): { colorStyle: any; borderColor: string; borderWidth: number } {
	let color: string;
	let borderColor: string;
	let borderWidth: number;

	if (stats.length > 1 && stat.includes('mod')) {
		color = '#fdaa56';
		borderColor = '#ff9933';
	} else if (aeroval.settings.regionalStatisticsSingleModelColor === 'theme') {
		color = '#d3d3d3';
		borderColor = '#808080';
	} else {
		const statistic = aeroval.statistics[statisticDir];
		let scale: number[];
		if (statistic?.scale == null) {
			scale = aeroval.ranges[parameterDir].scale;
		} else if (statistic.unit == 'var') {
			const statScale = aeroval.ranges[parameterDir].scale;
			scale = statistic.scale.map((x) => x * Math.max(...statScale));
			scale = scale.map((x) => parseFloat(x.toFixed(2)));
		} else {
			scale = statistic.scale;
		}
		color = value !== null ? colStat(value, scale, statisticDir) : '#d3d3d3';
		borderColor = color;
	}

	let colorStyle: any;
	if (isGlobalRegion) {
		colorStyle = {
			pattern: {
				path: { d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11' },
				strokeWidth: 3,
				width: 10,
				height: 10,
				color,
				opacity: 1,
			},
		};
		borderWidth = isSelectedRegion ? 2 : 1;
	} else {
		colorStyle = color;
		borderWidth = isSelectedRegion ? 2 : 0;
		borderColor = aeroval.settings.theme === 'light' ? 'black' : 'white';
	}

	return { colorStyle, borderColor, borderWidth };
}

function getRegionLabel(region: string): string | null {
	if (!(aeroval.cfg.webdisp_opts.country_flag || aeroval.cfg.webdisp_opts.country_code)) {
		return null;
	}

	if (aeroval.globalRegions.includes(region)) {
		return null;
	}

	let country = region;
	if (country === 'Macedonia') country = 'North Macedonia'
	if (country === 'Holy See (Vatican City State)') country = 'Vatican'

	if (!aeroval?.world_flags?.[country]) return null;

	if (aeroval.cfg.webdisp_opts.country_flag) return aeroval.world_flags[country].Emoji;

	if (aeroval.cfg.webdisp_opts.country_code) return aeroval.world_flags[country].ISO;

	return null;
}

function getRegionalStatisticValue(modData: any, region: string, keyTime: string, stat: string): number | null {
	let value: number | null = null;

	if (stat.includes('trend') && modData[region][keyTime][stat]) {
		if ((modData[region][keyTime][stat] as TrendsStats)['map_var']) {
			const mapVar = (modData[region][keyTime][stat] as TrendsStats)['map_var'];
			value = (modData[region][keyTime][stat] as TrendsStats)[mapVar];
		}
	} else if (stat === 'nrms') {
		value = (modData[region][keyTime]['rms'] as number) / Math.abs(modData[region][keyTime]['refdata_mean'] as number);
	} else {
		value = modData[region][keyTime][stat] as number;
	}

	if (value !== null && value !== undefined && aeroval.statistic.unit === '%') {
		value *= 100;
	}

	return value;
}

function getTitle(): string {
	var title = aeroval.parameter.name;
	if (aeroval.layer != 'Surface' && aeroval.layer != 'Column') title += ` (${aeroval.layer})`
	title += ` - ${aeroval.time}`;
	if (aeroval.season != 'All') title += ` (${aeroval.season})`
	return title;
}

